import React, { useEffect, useId, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

const COMMENTS_SHOW_PARAMS_CONFIG = 'platform_editor_comments_show_params';
const DEFAULT_COMMENT_SHOW_PARAMS = { collapseThreshold: 360, collapsedHeight: 200 };

const getCommentShowParams = (): { collapseThreshold: number; collapsedHeight: number } => {
	try {
		const params = JSON.parse(
			StatsigConfigurations.getString(
				COMMENTS_SHOW_PARAMS_CONFIG,
				JSON.stringify(DEFAULT_COMMENT_SHOW_PARAMS),
			),
		) as { collapseThreshold?: unknown; collapsedHeight?: unknown };
		return {
			collapseThreshold:
				typeof params.collapseThreshold === 'number'
					? params.collapseThreshold
					: DEFAULT_COMMENT_SHOW_PARAMS.collapseThreshold,
			collapsedHeight:
				typeof params.collapsedHeight === 'number'
					? params.collapsedHeight
					: DEFAULT_COMMENT_SHOW_PARAMS.collapsedHeight,
		};
	} catch {
		return DEFAULT_COMMENT_SHOW_PARAMS;
	}
};

const messages = defineMessages({
	showMore: {
		id: 'agent-mention.collapsible-comment-content.show-more',
		defaultMessage: 'Show more',
		description: 'Expand collapsed comment content to read it in full.',
	},
	showLess: {
		id: 'agent-mention.collapsible-comment-content.show-less',
		defaultMessage: 'Show less',
		description: 'Collapse comment content.',
	},
});

const styles = cssMap({
	collapsed: {
		overflow: 'hidden',
		// Fade into the comment's background, including selected and dark surfaces.
		maskImage: 'linear-gradient(to bottom, black calc(100% - 4rem), transparent)',
	},
	content: {
		display: 'flow-root',
	},
});

export const CollapsibleCommentContent = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { collapseThreshold, collapsedHeight } = getCommentShowParams();
	const contentId = useId();
	const contentRef = useRef<HTMLDivElement>(null);
	const toggleRef = useRef<HTMLButtonElement>(null);
	const [overflows, setOverflows] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const collapsed = overflows && !expanded;

	useEffect(() => {
		const content = contentRef.current;
		if (!content) return;
		const measure = () => setOverflows(content.scrollHeight > collapseThreshold);
		let resizeFrame: number | undefined;
		const scheduleMeasure = () => {
			if (resizeFrame !== undefined) return;
			resizeFrame = window.requestAnimationFrame(() => {
				resizeFrame = undefined;
				measure();
			});
		};
		measure();
		const observer =
			typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
		observer?.observe(content);
		window.addEventListener('resize', scheduleMeasure);
		return () => {
			observer?.disconnect();
			window.removeEventListener('resize', scheduleMeasure);
			if (resizeFrame !== undefined) window.cancelAnimationFrame(resizeFrame);
		};
	}, [collapseThreshold]);

	useEffect(() => {
		if (collapsed && document.activeElement === toggleRef.current) {
			toggleRef.current?.scrollIntoView({ block: 'nearest' });
		}
	}, [collapsed]);

	return (
		<Stack space="space.100">
			<Box
				id={contentId}
				xcss={collapsed ? styles.collapsed : undefined}
				style={{ maxHeight: collapsed ? `${collapsedHeight}px` : undefined }}
				onFocusCapture={() => {
					if (collapsed) setExpanded(true);
				}}
			>
				<Box ref={contentRef} xcss={styles.content}>
					{children}
				</Box>
			</Box>
			{overflows && (
				<Box>
					<Button
						ref={toggleRef}
						appearance="subtle"
						iconAfter={expanded ? ChevronUpIcon : ChevronDownIcon}
						aria-expanded={expanded}
						aria-controls={contentId}
						onClick={() => setExpanded(!expanded)}
					>
						{formatMessage(expanded ? messages.showLess : messages.showMore)}
					</Button>
				</Box>
			)}
		</Stack>
	);
};
